<template>
  <Page :provider-key="providerKey">
    <Heading>
      <i18n-t :keypath="error.headingKey" />
    </Heading>

    <div class="grid gap-2">
      <i18n-t tag="div" :keypath="error.messageKey">
        <template #link>
          <a class="underline" :href="t('link')" target="_blank">{{ t("link") }}</a>
        </template>
      </i18n-t>
      <i18n-t tag="div" :keypath="error.instructionsKey">
        <!--suppress VueUnrecognizedSlot -->
        <template #merchant>
          {{ merchantName }}
        </template>
      </i18n-t>
    </div>

    <slot name="details">
      <ErrorDetails :error="error" />
    </slot>

    <template #next>
      <FlowExitAction :label-override="t('actions.done')" outcome="canceled" />
    </template>
  </Page>
</template>

<script setup lang="ts">
  import { ErrorDetails } from "@versatile-credit-apply/components"
  import { useUserFriendlyError } from "@versatile-credit-apply/composition"
  import { type ProviderKey } from "@versatile-credit-apply/omni-apply-client"
  import { useMerchantName } from "@versatile-credit-apply/sdk"
  import { useI18n } from "vue-i18n"
  import { FlowExitAction } from "@/components/action"
  import { toRef } from "vue"

  interface Props {
    error?: unknown
  }

  const props = defineProps<Props>()

  defineSlots<{
    details: never
  }>()

  const { t } = useI18n()
  const merchantName = useMerchantName()

  const providerKey = "none" as ProviderKey

  const error = useUserFriendlyError({
    error: toRef(props, "error"),
  })
</script>

<i18n lang="yaml" locale="en">
errors:
  general:
    heading: Something went wrong
    instructions:
      at_home: Please try again later.
      in_store: Please see your {merchant} associate for assistance.
    message:
      user: We’re having some technical trouble on our end.
  unauthorized:
    heading: Unauthorized
    message:
      user: We could not establish a session.

page-title: ""
</i18n>

<i18n lang="yaml" locale="group_bdf_en">
errors:
  general:
    heading: Sorry!
    instructions:
      at_home: >
        Please call Bob Squad at 860-812-1111 for assistance completing your application or try again later to complete it online.
    message:
      user: >
        We’re sorry, financing on {link}  is temporarily unavailable.
link: https://www.mybobs.com
</i18n>
